import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { useDispatch } from "react-redux";

import { removeFormErrors } from "store/form/actions";

import { getOptions, getFirstOption, getBaseUnits, getUnitOptions } from "utils/fields";
import { maxLength, getValidation, getDecimalPlaces, required } from "utils/fieldValidation";
import { getDhwTemplate, getDhwEfDefault, getDefaultDhwSpecs } from "utils/mechanicals";
import { useStateSelector } from "utils/field";
import { createKeyboardShortcut } from "utils/keyboardShortcuts";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";
import DwhrSystem from "../DwhrSystem";
import Tooltip from "components/Tooltip";
import ThreeDots from "assets/images/icons/JSX/ThreeDots";
import DropDown from "components/Dropdown";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

import Delete from "assets/images/icons/JSX/Delete";
import Add from "assets/images/icons/JSX/Add";

const charLim100 = maxLength(100);
const dhwTankFractionValidation = getValidation("dhwTankFraction");
const dhwEnergySourceValidation = getValidation("dhwEnergySource");
const dhwTankTypeValidation = getValidation("dhwTankType");
const efValidation = getValidation("dhwEnergyFactor");
const tankLocationValidation = getValidation("dhwTankLocation");
const tankVolumeValidation = getValidation("dhwTankVolume");
const uefDrawPatternValidation = getValidation("dhwUniformEFDrawPattern");
const standbyLossValidation = getValidation("dhwStandbyHeatLoss");
const standbyLossModeValidation = getValidation("dhwStandbyHeatLossMode");
const thermalEffValidation = getValidation("dhwThermalEff");
const inputCapValidation = getValidation("dhwInputCapacity");

const insulatingBlanketValidation = getValidation("dhwInsulatingBlanket");
const pilotEnergyValidation = getValidation("dhwPilotEnergy");
const flueDiameterValidation = getValidation("dhwFlueDiameter");
const dhwHPCOPValidation = getValidation("dhwHPCOP");

const solarRatingValidation = getValidation("dhwSolarRating");
const solarAzimuthValidation = getValidation("dhwSolarAzimuth");
const solarSlopeValidation = getValidation("dhwSolarSlope");

export default ({
    accessor,
    change,
    isPrimary,
    handleDelete,
    noBasementComponents,
    isUpgrade = false,
    formName,
    isDwhrParametric = false,
    isPrimaryDhwParametric = false,
    hasSecondarySystem = false,
    isSecondary = false,
}) => {
    const dispatch = useDispatch();
    const [menuOpen, toggleMenu] = useState(false);

    const { id: energySourceId } = useStateSelector(`${accessor}.energySource`) || {};
    const { id: tankTypeId } = useStateSelector(`${accessor}.tankType`) || {};
    const { id: tankVolumeId, value: tankVolumeValue } = useStateSelector(`${accessor}.tankVolume`) || {};
    const isUniformEF = useStateSelector(`${accessor}.isUniform`);
    const { id: efId, value: efValue } = useStateSelector(`${accessor}.energyFactor`) || {};
    const {
        inputCapacity: inputCapValue,
        standbyHeatLoss: standbyHeatLossValue,
        standbyHeatLossMode: standbyHeatLossModeValue,
        thermalEfficiency: thermalEfficiencyValue,
    } = useStateSelector(`${accessor}.standby`) || {};

    const insulatingBlanketValue = useStateSelector(`${accessor}.insulatingBlanket`);
    const pilotEnergyValue = useStateSelector(`${accessor}.pilotEnergy`);
    const flueDiameterValue = useStateSelector(`${accessor}.flueDiameter`);
    const hpCopValue = useStateSelector(`${accessor}.heatPumpCOP`);
    const { id: dhwTankLocationId } = useStateSelector(`${accessor}.tankLocation`);

    const isSolarEmpty = isEmpty(useStateSelector(`${accessor}.solar`) || {});
    const isDwhr = !isEmpty(useStateSelector(`${accessor}.drainWaterHeatRecovery`) || {});

    //standbyHeatLossMode => 0 = W, 1 = %/hr
    const standbyHeatLossUnits = useStateSelector(`${accessor}.standby.standbyHeatLoss_u`);
    const tankVolumeUnits = useStateSelector(`${accessor}.tankVolume_u`);
    const thermalEffUnits = useStateSelector(`${accessor}.standby.thermalEfficiency_u`);
    const inputCapUnits = useStateSelector(`${accessor}.standby.inputCapacity_u`);
    const insulatingBlanketUnits = useStateSelector(`${accessor}.insulatingBlanket_u`);
    const pilotEnergyUnits = useStateSelector(`${accessor}.pilotEnergy_u`);
    const flueDiameterUnits = useStateSelector(`${accessor}.flueDiameter_u`);

    const solarRatingUnits = useStateSelector(`${accessor}.solar.rating_u`);
    const solarSlopeUnits = useStateSelector(`${accessor}.solar.slope_u`);
    const solarAzimuthUnits = useStateSelector(`${accessor}.solar.azimuth_u`);
    const fractionOfTankUnits = useStateSelector(`${accessor}.fractionOfTank_u`);

    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};

    const { id: buildingTypeId } = useStateSelector(`model.modelData.specifications.buildingType`) || {};
    const isMURB = buildingTypeId === 2; //Whole building MURB

    const heatingSystemType = Object.keys(useStateSelector("model.modelData.heatingCooling.heating.system") || {})[0];

    let primaryAccessor = accessor;
    if (primaryAccessor.includes("secondarySystem")) {
        primaryAccessor = primaryAccessor.split(".secondarySystem")[0] + ".primarySystem";
    }

    const isPrimarySolar = !isEmpty(useStateSelector(`${primaryAccessor}.solar`) || {});
    const fractionOfTankValue = useStateSelector(`${primaryAccessor}.fractionOfTank`);

    const efOpts = isUniformEF
        ? [
              {
                  label: "User Specified",
                  value: {
                      id: 1,
                      value: efValue,
                  },
              },
          ]
        : [
              {
                  label: "Use Defaults",
                  value: {
                      id: 0,
                      value: efValue,
                  },
              },
              {
                  label: "User Specified",
                  value: {
                      id: 1,
                      value: efValue,
                  },
              },
              {
                  label: "Standby",
                  value: {
                      id: 2,
                      value: efValue,
                  },
              },
          ];

    // H2k ignores the energy factor field when standby inputs are used, so this estimate is
    // displayed only to let users compare standby-rated tanks against EF-rated ones.
    const getStandbyEquivalentEf = () => {
        //HOT2000 prescribes an EF of 0.9 for all integrated heat pump tanks, with the unit's
        //rated efficiency captured through the heat pump COP instead
        if (tankTypeId === 5) {
            return 0.9;
        }

        const thermalEffFraction = parseFloat(thermalEfficiencyValue) / 100;
        const inputCapacity = parseFloat(inputCapValue);
        const standbyHeatLoss = parseFloat(standbyHeatLossValue);
        const tankVolume = parseFloat(tankVolumeValue);

        if (!thermalEffFraction || !inputCapacity || isNaN(standbyHeatLoss)) {
            return 0;
        }

        //From H2k: SL(W) = 0.432 * SL(%/hr) * V(L)
        const standbyHeatLossWatts =
            standbyHeatLossModeValue === 1 ? 0.432 * standbyHeatLoss * (tankVolume || 0) : standbyHeatLoss;

        //HOT2000 assumes a daily hot water draw of 43.3 kWh
        const dailyDraw = 43.3;
        const denominator =
            dailyDraw / thermalEffFraction +
            standbyHeatLossWatts * (0.084 - dailyDraw / (thermalEffFraction * inputCapacity));

        if (denominator <= 0) {
            return 0;
        }

        const equivalentEf = dailyDraw / denominator;

        return isFinite(equivalentEf) && equivalentEf > 0 ? parseFloat(equivalentEf.toFixed(4)) : 0;
    };

    useEffect(() => {
        if (efId !== 2) {
            return;
        }

        const equivalentEf = getStandbyEquivalentEf();

        if (equivalentEf !== efValue) {
            change(`${accessor}.energyFactor.value`, equivalentEf);
        }
    }, [
        efId,
        tankTypeId,
        inputCapValue,
        standbyHeatLossValue,
        standbyHeatLossModeValue,
        thermalEfficiencyValue,
        tankVolumeValue,
    ]);

    const handleSolarInit = (id) => {
        if (isSolarEmpty) {
            change(`${accessor}.solar`, {
                rating: 6350,
                slope: 51.19,
                azimuth: 0,
            });
        }
        change(`${accessor}.energyFactor`, { id: 0, value: 0 });
        change(`${accessor}.tankVolume`, { id: 6, value: 0 });
    };

    const tanklessIds = [2, 3, 8, 9, 10, 11, 18, 21, 22, 23, 24];

    const handleTankTypeChange = (id) => {
        //Handle disabling tank volume
        if (id === 5 && (hpCopValue == null || isNaN(hpCopValue))) {
            change(`${accessor}.heatPumpCOP`, 2.5);
            change(`${accessor}.energyFactor`, { id: 1, value: getDhwEfDefault({ tankTypeId: id, tankVolId: 6 }) });
        } else if (tanklessIds.includes(id)) {
            change(`${accessor}.tankVolume`, { id: 6, value: 0 });
            change(`${accessor}.energyFactor.value`, getDhwEfDefault({ tankTypeId: id, tankVolId: 6 }));
        } else if (id <= 53 || id >= 30) {
            change(`${accessor}.energyFactor.value`, getDhwEfDefault({ tankTypeId: id, tankVolId: 6 }));
        }

        const { pilotEnergy = 0, flueDiameter = 0 } = getDefaultDhwSpecs(id);

        change(`${accessor}.pilotEnergy`, pilotEnergy);
        change(`${accessor}.flueDiameter`, flueDiameter);

        if (!tanklessIds.includes(id)) {
            change(`${accessor}.tankVolume`, { id: 2, value: 151.4 });
        }
    };

    const handleTankVolumeChange = (id) => {
        if (efId === 0) {
            change(`${accessor}.energyFactor.value`, getDhwEfDefault({ tankTypeId, tankVolId: id }));
        }
    };

    const handleCreateDwhr = () => {
        change(`${accessor}.hasDrainWaterHeatRecovery`, true);
        change(`${accessor}.drainWaterHeatRecovery`, getDhwTemplate("dwhr"));
        if (isMURB) {
            change(`${accessor}.connectedUnitsDwhr`, 1);
        }
    };

    const handleDeleteDwhr = () => {
        change(`${accessor}.hasDrainWaterHeatRecovery`, false);
        change(`${accessor}.drainWaterHeatRecovery`, {});
        dispatch(removeFormErrors(`${accessor}.drainWaterHeatRecovery`));
        dispatch(removeFormErrors(`${accessor}.connectedUnitsDwhr`));
    };

    const isPilotDisabled = ![1, 3].includes(energySourceId) || ![7, 11, 13, 15, 20, 24, 26, 28].includes(tankTypeId);

    if (isDwhrParametric) {
        return (
            <div
                className={`${!isUpgrade && sharedClasses.section} ${!isPrimary && classes.sectionSecondary}`}
                style={{ marginTop: isUpgrade ? "1.875rem" : "" }}
            >
                {isDwhr && (
                    <DwhrSystem
                        accessor={`${accessor}.drainWaterHeatRecovery`}
                        change={change}
                        handleDelete={() => handleDeleteDwhr()}
                        formName={formName}
                        isDwhrParametric={isDwhrParametric}
                    />
                )}
            </div>
        );
    }

    return (
        <div
            className={`${!isUpgrade && sharedClasses.section} ${!isPrimary && classes.sectionSecondary}`}
            style={{ marginTop: isUpgrade ? "1.875rem" : "" }}
        >
            <div className={classes.heading}>
                <div className={classes.title}>
                    <h4>{isPrimary ? "Primary" : "Secondary"} Hot Water System</h4>
                    <span
                        className={classes.menu}
                        onClick={() => toggleMenu(!menuOpen)}
                        {...createKeyboardShortcut({ onKeyDown: () => toggleMenu(!menuOpen) })}
                    >
                        <ThreeDots />
                    </span>
                    <DropDown
                        className={classes.dropDown}
                        open={menuOpen}
                        toggleOpen={toggleMenu}
                        options={[
                            {
                                label: (
                                    <span>
                                        <Delete /> Delete
                                    </span>
                                ),
                                className: classes.delete,
                                onClick: handleDelete,
                            },
                        ]}
                    />
                </div>
                {!isPrimaryDhwParametric && (
                    <div
                        className={`${classes.addDHWR} ${isDwhr && classes.disabled}`}
                        onClick={() => handleCreateDwhr()}
                        data-tip="Add Drain Water Heat Recovery"
                        data-for="dwhrHeader"
                        {...createKeyboardShortcut({ onKeyDown: handleCreateDwhr })}
                    >
                        Add DWHR <Add />
                    </div>
                )}

                <Tooltip id="dwhrHeader" />
            </div>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={Select}
                    name={`${accessor}.energySource`}
                    options={getOptions({ fieldKey: "dhwEnergySource" })}
                    label="Energy Source"
                    placeholder="Choose Energy Source"
                    validate={dhwEnergySourceValidation}
                    onChange={({ id }) => {
                        change(`${accessor}.tankType`, getFirstOption({ indKey: id, depKey: "dhwTankType" }));
                        if (id === 8) {
                            handleSolarInit(id);
                        } else if (energySourceId > 3) {
                            change(`${accessor}.tankVolume`, { id: 6, value: 0 });
                        } else if (tanklessIds.includes(tankTypeId)) {
                            change(`${accessor}.tankVolume`, { id: 2, value: 151.4 });
                        }

                        if (id === 0 && efId === 2) {
                            change(`${accessor}.standby.thermalEfficiency`, 98);
                        } else if (efId === 2) {
                            change(`${accessor}.standby.thermalEfficiency`, 0);
                        }

                        if (id >= 4) {
                            change(`${accessor}.isUniform`, false);
                        }
                    }}
                />
                <Field
                    component={Select}
                    name={`${accessor}.tankType`}
                    options={getOptions({ fieldKey: "dhwTankType", indKey: energySourceId })}
                    label="Tank Type"
                    placeholder="Choose Tank Type"
                    validate={dhwTankTypeValidation}
                    onChange={({ id }) => handleTankTypeChange(id)}
                    info={
                        "For heat pump water heaters, only the Integrated Heat Pump option may be modelled in HOT2000."
                    }
                    warning={[3, 4].includes(tankTypeId) ? "hpwhWorkaroundError" : ""}
                />
                <Field
                    component={Input}
                    name={`${accessor}.manufacturer`}
                    type="text"
                    label="Manufacturer Name"
                    placeholder="Enter Manufacturer Name"
                    customValidateFunc={charLim100}
                />
                <Field
                    component={Input}
                    name={`${accessor}.model`}
                    type="text"
                    label="Model Name"
                    placeholder="Enter Model Name"
                    customValidateFunc={charLim100}
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={Select}
                    name={`${accessor}.tankLocation`}
                    options={getOptions({ fieldKey: "dhwTankLocation" })}
                    label="Tank Location"
                    placeholder="Choose Tank Location"
                    validate={tankLocationValidation}
                    warning={noBasementComponents && dhwTankLocationId === 1 ? "dhwNoBasement" : ""}
                />
                <Field
                    component={Select}
                    name={`${accessor}.tankVolume`}
                    options={[
                        {
                            label: "User specified",
                            value: {
                                id: 0,
                                value: tankVolumeValue,
                            },
                        },
                        ...getOptions({ fieldKey: "dhwTankVolume" }),
                    ]}
                    label="Tank Volume"
                    placeholder="Choose Tank Volume"
                    validate={tankVolumeValidation}
                    disabled={energySourceId > 3 || [2, 3, 4, 8, 9, 10, 11, 18, 21, 22, 23, 24].includes(tankTypeId)}
                    onChange={({ id }) => handleTankVolumeChange(id)}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.tankVolume.value`}
                    label="Volume"
                    placeholder={0.0}
                    disabled={
                        tankVolumeId !== 0 ||
                        energySourceId > 3 ||
                        [2, 3, 4, 8, 9, 10, 11, 18, 21, 22, 23, 24].includes(tankTypeId)
                    }
                    validate={tankVolumeValidation}
                    setTouched={true}
                    decimals={getDecimalPlaces("dhwTankVolume")}
                    change={change}
                    setValue={tankVolumeValue}
                    units={{
                        base: getBaseUnits("dhwTankVolume", modelUnits),
                        options: getUnitOptions("volume"),
                        selected: tankVolumeUnits,
                        unitType: "volume",
                        accessor: `${accessor}.tankVolume_u`,
                    }}
                />
            </InputRow>
            <InputRow gridTemplate="23.25% 23.25% 15% 15% 15%">
                <Field
                    component={Select}
                    name={`${accessor}.isUniform`}
                    options={[
                        {
                            label: "Uniform Energy Factor",
                            value: true,
                        },
                        {
                            label: "Energy Factor",
                            value: false,
                        },
                    ]}
                    label="Efficiency Type"
                    placeholder="Choose Efficiency Type"
                    info={"Select based on the available efficiency type data. If unknown, select Energy Factor."}
                    onChange={(value) => {
                        if (value) {
                            change(`${accessor}.energyFactor`, { id: 1, value: efValue });
                            change(`${accessor}.drawPattern`, { id: 2 });
                        }
                    }}
                />
                <Field
                    component={Select}
                    name={`${accessor}.energyFactor`}
                    options={efOpts}
                    label="Input Type"
                    placeholder="Choose Input Factor"
                    validate={efValidation}
                    disabled={energySourceId === 8}
                    onChange={({ id }) => {
                        if (energySourceId === 0 && id === 2) {
                            change(`${accessor}.standby.thermalEfficiency`, 98);
                        } else {
                            change(`${accessor}.standby.thermalEfficiency`, 0);
                        }
                    }}
                />
                <Field
                    component={Input}
                    name={`${accessor}.energyFactor.value`}
                    type="number"
                    label={isUniformEF ? "Uniform Energy Factor" : "Energy Factor"}
                    placeholder={0.0}
                    setValue={efValue}
                    validate={efValidation}
                    disabled={efId !== 1}
                    info={
                        efId !== 2
                            ? ""
                            : tankTypeId === 5
                            ? "Prescribed EF of 0.9 that HOT2000 requires for all heat pump water heater tanks"
                            : "Estimated equivalent EF based on standby loss inputs and HOT2000 assumptions"
                    }
                />
                <Field
                    component={Checkbox}
                    name={`${accessor}.energyStar`}
                    label="Energy Star"
                    large
                    className={classes.checkbox}
                    type="checkbox"
                />
                <Field
                    component={Checkbox}
                    name={`${accessor}.ecoEnergy`}
                    label="ecoEnergy"
                    large
                    className={classes.checkbox}
                    type="checkbox"
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.standby.inputCapacity`}
                    label="Input Capacity"
                    placeholder="Enter Input Capacity"
                    highlight={efId === 2}
                    validate={[required, ...inputCapValidation]}
                    setValue={inputCapValue}
                    setTouched={true}
                    decimals={getDecimalPlaces("dhwInputCapacity")}
                    change={change}
                    units={{
                        base: getBaseUnits("dhwInputCapacity", modelUnits),
                        options: getUnitOptions("power"),
                        selected: inputCapUnits,
                        unitType: "power",
                        accessor: `${accessor}.standby.inputCapacity_u`,
                    }}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.standby.thermalEfficiency`}
                    label="Thermal Efficiency"
                    placeholder={0.0}
                    highlight={efId === 2}
                    disabled={efId !== 2 || [0, 8].includes(energySourceId)}
                    validate={thermalEffValidation}
                    setValue={thermalEfficiencyValue}
                    setTouched={true}
                    decimals={getDecimalPlaces("dhwThermalEff")}
                    change={change}
                    units={{
                        base: getBaseUnits("dhwThermalEff", modelUnits),
                        options: getUnitOptions("fraction"),
                        selected: thermalEffUnits,
                        unitType: "fraction",
                        accessor: `${accessor}.standby.thermalEfficiency_u`,
                    }}
                />
                {isUniformEF && (
                    <Field
                        component={Select}
                        name={`${accessor}.drawPattern`}
                        options={getOptions({ fieldKey: "dhwUniformEFDrawPattern" })}
                        label="UEF Draw Pattern"
                        placeholder="Choose Draw Pattern"
                        validate={uefDrawPatternValidation}
                        disabled={energySourceId >= 4}
                    />
                )}
                {efId === 2 ? (
                    <>
                        {standbyHeatLossModeValue === 1 ? (
                            <>
                                <Field
                                    component={Input}
                                    name={`${accessor}.standby.standbyHeatLoss`}
                                    type="number"
                                    label="Standby Heat Loss"
                                    placeholder={"Enter value"}
                                    setValue={standbyHeatLossValue}
                                    highlight={efId === 2}
                                    validate={standbyLossValidation}
                                    disabled={efId !== 2}
                                />
                            </>
                        ) : (
                            <>
                                <Field
                                    component={InputWithUnits}
                                    type="number"
                                    name={`${accessor}.standby.standbyHeatLoss`}
                                    label="Standby Heat Loss"
                                    placeholder={"Enter value"}
                                    highlight={efId === 2}
                                    disabled={efId !== 2}
                                    validate={standbyLossValidation}
                                    setTouched={true}
                                    decimals={getDecimalPlaces("dhwStandbyHeatLoss")}
                                    change={change}
                                    setValue={standbyHeatLossValue}
                                    units={{
                                        base: getBaseUnits("dhwStandbyHeatLoss", modelUnits),
                                        options: getUnitOptions("power"),
                                        selected: standbyHeatLossUnits,
                                        unitType: "power",
                                        accessor: `${accessor}.standby.standbyHeatLoss_u`,
                                    }}
                                />
                            </>
                        )}
                        <Field
                            component={Select}
                            name={`${accessor}.standby.standbyHeatLossMode`}
                            type="number"
                            label="Standby Heat Loss Input Type"
                            placeholder="Choose Input Type"
                            options={[
                                {
                                    label: "Power Units",
                                    value: 0,
                                },
                                {
                                    label: "%/hr",
                                    value: 1,
                                },
                            ]}
                            setValue={standbyHeatLossModeValue}
                            highlight={efId === 2}
                            validate={standbyLossModeValidation}
                            disabled={efId !== 2}
                        />
                    </>
                ) : (
                    <></>
                )}
            </InputRow>
            {![0, 8].includes(energySourceId) ? (
                <>
                    <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.insulatingBlanket`}
                            label="Insulating Blanket"
                            placeholder={0.0}
                            disabled={energySourceId === 8 || [3, 4].includes(tankTypeId)}
                            validate={insulatingBlanketValidation}
                            setTouched={true}
                            setValue={insulatingBlanketValue}
                            decimals={getDecimalPlaces("dhwInsulatingBlanket")}
                            change={change}
                            units={{
                                base: getBaseUnits("dhwInsulatingBlanket", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: insulatingBlanketUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.insulatingBlanket_u`,
                            }}
                            info={"Enter a value of 0 to indicate that no insulating blanket is present."}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.pilotEnergy`}
                            label="Pilot Energy"
                            placeholder={0.0}
                            disabled={isPilotDisabled}
                            validate={pilotEnergyValidation}
                            setTouched={true}
                            setValue={isPilotDisabled ? 0 : pilotEnergyValue}
                            decimals={getDecimalPlaces("dhwPilotEnergy")}
                            change={change}
                            units={{
                                base: getBaseUnits("dhwPilotEnergy", modelUnits),
                                options: getUnitOptions("dailyEnergy"),
                                selected: pilotEnergyUnits,
                                unitType: "dailyEnergy",
                                accessor: `${accessor}.pilotEnergy_u`,
                            }}
                            onChange={(value) => {
                                if (value !== 0) {
                                    change(`${accessor}.userDefinedPilot`, true);
                                } else {
                                    change(`${accessor}.userDefinedPilot`, false);
                                }
                            }}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.flueDiameter`}
                            label="Flue Diameter"
                            placeholder={0.0}
                            disabled={
                                ![1, 2, 3].includes(energySourceId) || [8, 14, 15, 18, 21, 27, 28].includes(tankTypeId)
                            }
                            validate={flueDiameterValidation}
                            setTouched={true}
                            setValue={flueDiameterValue}
                            decimals={getDecimalPlaces("dhwFlueDiameter")}
                            change={change}
                            units={{
                                base: getBaseUnits("dhwFlueDiameter", modelUnits),
                                options: getUnitOptions("length"),
                                selected: flueDiameterUnits,
                                unitType: "length",
                                accessor: `${accessor}.flueDiameter_u`,
                            }}
                            info={"Leave as the default value."}
                        />
                        <Field
                            component={Checkbox}
                            name={`${accessor}.combinedFlue`}
                            label="Combined with heating system flue?"
                            large
                            className={classes.checkbox}
                            type="checkbox"
                            disabled={!["furnace", "boiler"].includes(heatingSystemType)}
                        />
                    </InputRow>
                </>
            ) : (
                <></>
            )}
            {energySourceId === 0 ? (
                <>
                    <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.insulatingBlanket`}
                            label="Insulating Blanket"
                            placeholder={0.0}
                            disabled={energySourceId === 8 || [3, 4].includes(tankTypeId)}
                            validate={insulatingBlanketValidation}
                            setTouched={true}
                            setValue={insulatingBlanketValue}
                            decimals={getDecimalPlaces("dhwInsulatingBlanket")}
                            change={change}
                            units={{
                                base: getBaseUnits("dhwInsulatingBlanket", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: insulatingBlanketUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.insulatingBlanket_u`,
                            }}
                        />
                        <Field
                            component={Input}
                            type="number"
                            name={`${accessor}.heatPumpCOP`}
                            label="Heat Pump COP"
                            placeholder={"Enter Heat Pump COP"}
                            disabled={tankTypeId !== 5}
                            validate={dhwHPCOPValidation}
                            decimals={getDecimalPlaces("dhwHPCOP")}
                            setValue={!isNaN(hpCopValue) ? hpCopValue : null} //Only set value if it's nonNaN
                            info={"Only applicable with an Integrated Heat Pump"}
                        />
                    </InputRow>
                </>
            ) : (
                <></>
            )}
            {energySourceId === 8 && (
                <>
                    <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.solar.rating`}
                            label="CSA F379 Rating (per year)"
                            placeholder={0.0}
                            disabled={energySourceId !== 8}
                            validate={solarRatingValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("dhwSolarRating")}
                            change={change}
                            units={{
                                base: getBaseUnits("dhwSolarRating", modelUnits),
                                options: getUnitOptions("energy"),
                                selected: solarRatingUnits,
                                unitType: "energy",
                                accessor: `${accessor}.solar.rating_u`,
                            }}
                            info={"Enter either the CSA F379 rating or SRCC Canadian One-Day Performance Rating."}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.solar.slope`}
                            label="Solar Slope"
                            placeholder={0.0}
                            disabled={energySourceId !== 8}
                            validate={solarSlopeValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("dhwSolarSlope")}
                            change={change}
                            units={{
                                base: ["°"],
                                options: ["°"],
                                selected: ["°"],
                                unitType: "angle",
                                accessor: `${accessor}.solar.slope_u`,
                            }}
                            info={"As measured from the horizontal."}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.solar.azimuth`}
                            label="Solar Azimuth"
                            placeholder={0.0}
                            disabled={energySourceId !== 8}
                            validate={solarAzimuthValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("dhwSolarAzimuth")}
                            change={change}
                            units={{
                                base: ["°"],
                                options: ["°"],
                                selected: ["°"],
                                unitType: "angle",
                                accessor: `${accessor}.solar.azimuth_u`,
                            }}
                            info={
                                "Relative to true south, where: South = 0°, East = 90°, West = -90°, and North = ±180°."
                            }
                        />
                    </InputRow>
                </>
            )}
            {(hasSecondarySystem || isSecondary) && !isPrimarySolar && (
                <InputRow gridTemplate="1fr 1fr 1fr 1fr" lastRow={isSecondary ? false : !isDwhr}>
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.fractionOfTank`}
                        label="Fraction of Tank"
                        placeholder={0.0}
                        disabled={!hasSecondarySystem}
                        validate={[
                            ...(isPrimary && hasSecondarySystem ? [required] : []),
                            ...dhwTankFractionValidation,
                        ]}
                        setTouched={true}
                        decimals={getDecimalPlaces("dhwTankFraction")}
                        change={change}
                        setValue={isPrimary ? fractionOfTankValue : 1 - fractionOfTankValue}
                        units={{
                            base: getBaseUnits("dhwTankFraction", modelUnits),
                            options: getUnitOptions("fraction"),
                            selected: fractionOfTankUnits,
                            unitType: "fraction",
                            accessor: `${accessor}.fractionOfTank_u`,
                        }}
                    />
                </InputRow>
            )}
            {isDwhr && (
                <DwhrSystem
                    accessor={`${accessor}.drainWaterHeatRecovery`}
                    change={change}
                    handleDelete={() => handleDeleteDwhr()}
                    formName={formName}
                />
            )}
            {isSecondary && isDwhr && <div style={{ marginBottom: "1.25rem" }} />}
        </div>
    );
};
